/**
 * Cross-field evidence invariants.
 *
 * Per-field schemas (see `evidence.ts`) can only say "this key is required".
 * They cannot say "if outcome is partial then a note is required" or "an
 * adverse event without an incident reference is incoherent". Those rules are
 * the ones that stop a partial, refused or adverse outcome being recorded as
 * if nothing happened — and they live here.
 *
 * A violation means the combination is incoherent and must not commit.
 * Returning an empty array means the bundle is coherent (not that every
 * per-field requirement is satisfied — that is a separate check).
 */

import type { OverlayCode, ServicePackCode } from './packs';
import { capturesReceiptEvidence } from './packs';

// ---------------------------------------------------------------------------
// Violation shape
// ---------------------------------------------------------------------------

/**
 * What the caller must state about the occurrence before evidence can be judged.
 *
 * `activeOverlays` has no default on purpose. An overlay is the declared "this
 * client is at risk" signal, never implied by a category, label or diagnosis.
 * An optional parameter defaulting to `[]` would mean every caller that simply
 * forgot it silently asserted "no risk applies" — inference by omission, which
 * is the one thing the overlay design forbids. Making it required costs a
 * keystroke and forces whoever writes the next caller to go and find the real
 * answer. Pass `[]` only when you know there are none.
 */
export interface EvidenceInvariantContext {
  /** Overlays switched on for this occurrence by an active, applicable record. */
  activeOverlays: readonly OverlayCode[];
  /**
   * Keys the bundle explicitly claimed as not applicable. Some rules must tell
   * "not supplied" from "declared not applicable": marking a manual-handling
   * transfer method N/A while recording full physical assistance is a
   * deliberate bypass, not an omission, and reads differently.
   */
  notApplicableFields?: readonly string[];
}

export interface EvidenceInvariantViolation {
  pack: ServicePackCode;
  /** Stable snake_case code — downstream code routes on this. */
  rule: string;
  /** The field that must be supplied (or set to the required value). */
  requiredField: string;
  /** The field/value that triggered the requirement. */
  because: string;
}

// ---------------------------------------------------------------------------
// Trigger sets
// ---------------------------------------------------------------------------

/**
 * Delivery outcomes that are not a clean completion. Without a variance note
 * they are indistinguishable from DELIVERED in every downstream report.
 */
const ADVERSE_DELIVERY_OUTCOMES = [
  'PARTIALLY_DELIVERED',
  'REFUSED',
  'NO_CONTACT',
  'UNSAFE_TO_PROCEED',
  // CANCELLED is a declared delivery_outcome and was missing here, so every
  // rule keyed on this set skipped it: a cancelled visit needed no variance
  // note, and a cancelled visit carrying ten billable items or a positive
  // receipt amount produced zero violations. The service did not happen —
  // that is the plainest non-delivery there is.
  'CANCELLED',
  'EXCEPTION',
] as const;

/**
 * Exported so a test can compare this set against the DECLARED enum.
 *
 * Every `delivery_outcome` value except DELIVERED is a non-delivery and belongs
 * in the set above. Saying so in a comment is what failed last time: CANCELLED
 * was absent for the whole of phases 3 and 4 while the prose read as though the
 * set were complete. The guard lives in the test suite, where it can actually
 * fail, not in this sentence.
 */
export const NON_DELIVERED_OUTCOME_VALUES: readonly string[] = ADVERSE_DELIVERY_OUTCOMES;

type AdverseDeliveryOutcome = (typeof ADVERSE_DELIVERY_OUTCOMES)[number];

const ADVERSE_DELIVERY_OUTCOME_SET: ReadonlySet<string> = new Set(ADVERSE_DELIVERY_OUTCOMES);

/**
 * Escalations that leave the ordinary supervisory path. A clinical or emergency
 * escalation with no written explanation is an unaccountable hand-off.
 */
const NOTE_REQUIRING_ESCALATIONS = ['CLINICAL_ESCALATION', 'EMERGENCY_SERVICES'] as const;

type NoteRequiringEscalation = (typeof NOTE_REQUIRING_ESCALATIONS)[number];

const NOTE_REQUIRING_ESCALATION_SET: ReadonlySet<string> = new Set(NOTE_REQUIRING_ESCALATIONS);

/**
 * Packs whose adverse-event flag must resolve to a canonical incident record.
 * Free-text adverse events are invisible to incident reporting.
 */
/**
 * Only packs that actually declare the adverse-event fields belong here.
 *
 * `ALLIED_PROFESSIONAL` was listed while its schema declared neither field, so
 * the rule was inert — a registered safety rule that could never fire, which is
 * worse than an absent one because a reader counts it as covered. It was
 * removed, and the underlying gap (an allied professional with no way to record
 * an adverse event at all) went to the ledger as a schema question.
 *
 * RULING (Paul, 2026-07-28): allied professionals need that route. The fields
 * are now declared on the pack, so the rule is restored — this time with the
 * schema behind it. The guard test keeps this set honest either way.
 */
const ADVERSE_EVENT_PACKS: ReadonlySet<ServicePackCode> = new Set([
  'THERAPEUTIC_PROFESSIONAL',
  'ALLIED_PROFESSIONAL',
]);

/**
 * Packs where an unsafe site (or a found hazard) must leave a variance note.
 */
const SITE_SAFETY_PACKS: ReadonlySet<ServicePackCode> = new Set(['DOMESTIC', 'MAINTENANCE_WORK']);

// ---------------------------------------------------------------------------
// Field presence
// ---------------------------------------------------------------------------

/**
 * Missing, explicitly null and whitespace-only strings all mean "not
 * supplied". Any other value (including `false` and `0`) is present — boolean
 * rules use `=== true` / `=== false` themselves so they are not confused with
 * presence.
 */
/**
 * Reads a quantity that may arrive as a number or as a numeric string.
 *
 * Evidence crosses JSON and form boundaries, so a `typeof === 'number'` guard
 * turns a finance control into a no-op the moment legs arrive as "2". Returns
 * null when the value is not a finite number in either shape.
 */
function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isSupplied(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function pushViolation(
  out: EvidenceInvariantViolation[],
  pack: ServicePackCode,
  rule: string,
  requiredField: string,
  because: string,
): void {
  out.push({ pack, rule, requiredField, because });
}

// ---------------------------------------------------------------------------
// Universal rules (every pack)
// ---------------------------------------------------------------------------

/**
 * Partial, refused, no-contact, unsafe and exception outcomes need a written
 * explanation. Without one the completion looks identical to a clean delivery
 * in every report built from the outcome enum alone.
 */
function checkAdverseDeliveryOutcome(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  const outcome = fields.delivery_outcome;
  if (typeof outcome !== 'string' || !ADVERSE_DELIVERY_OUTCOME_SET.has(outcome)) return;
  if (isSupplied(fields.material_variance_note)) return;

  pushViolation(
    out,
    pack,
    'delivery_outcome_requires_material_variance_note',
    'material_variance_note',
    `delivery_outcome=${outcome as AdverseDeliveryOutcome}`,
  );
}

/**
 * Clinical and emergency escalations require a material variance note so the
 * hand-off is accountable and reviewable.
 */
function checkEscalationDisposition(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  const disposition = fields.escalation_disposition;
  if (typeof disposition !== 'string' || !NOTE_REQUIRING_ESCALATION_SET.has(disposition)) {
    return;
  }
  if (isSupplied(fields.material_variance_note)) return;

  pushViolation(
    out,
    pack,
    'escalation_disposition_requires_material_variance_note',
    'material_variance_note',
    `escalation_disposition=${disposition as NoteRequiringEscalation}`,
  );
}

// ---------------------------------------------------------------------------
// Pack-scoped rules
// ---------------------------------------------------------------------------

/**
 * An adverse event left only in free text is invisible to incident reporting.
 * The incident reference is the durable link into the canonical module.
 */
function checkAdverseEvent(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (!ADVERSE_EVENT_PACKS.has(pack)) return;
  // Only an explicit true fires — a missing boolean is the per-field required
  // check's job, not this module's.
  if (fields.adverse_event_occurred !== true) return;
  if (isSupplied(fields.adverse_event_incident_ref)) return;

  pushViolation(
    out,
    pack,
    'adverse_event_requires_incident_ref',
    'adverse_event_incident_ref',
    'adverse_event_occurred=true',
  );
}

/**
 * Medication self-admin assist exists so observed discrepancies are explained
 * and escalated. An observed discrepancy that nobody escalated, or a client
 * who did not self-administer with no detail, is the defect this pack
 * prevents.
 */
function checkMedicationSelfAdmin(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'MEDICATION_SELF_ADMIN_ASSIST') return;

  if (fields.discrepancy_observed === true) {
    if (!isSupplied(fields.discrepancy_detail)) {
      pushViolation(
        out,
        pack,
        'discrepancy_observed_requires_discrepancy_detail',
        'discrepancy_detail',
        'discrepancy_observed=true',
      );
    }
    // Consequence is "must be true", not merely "must be supplied". Missing or
    // false both fail; only an explicit true satisfies the rule.
    if (fields.escalated !== true) {
      pushViolation(
        out,
        pack,
        'discrepancy_observed_requires_escalation',
        'escalated',
        'discrepancy_observed=true',
      );
    }
  }

  if (fields.client_self_administered === false) {
    if (!isSupplied(fields.discrepancy_detail)) {
      pushViolation(
        out,
        pack,
        'client_self_administered_false_requires_discrepancy_detail',
        'discrepancy_detail',
        'client_self_administered=false',
      );
    }

    // S3.1 — a dose the client did not take is a clinical event, not a note.
    // Detail alone leaves it in this bundle's free text, where the medication
    // module never sees it and no one is accountable for the next dose. Either
    // the event is linked to the canonical record or somebody escalated it.
    if (!isSupplied(fields.medication_event_ref) && fields.escalated !== true) {
      pushViolation(
        out,
        pack,
        'client_self_administered_false_requires_event_ref_or_escalation',
        'medication_event_ref',
        'client_self_administered=false',
      );
    }
  }

  // S3.1 — an escalation with nothing attached cannot be acted on. Whoever
  // receives it needs either the observed detail or the canonical event.
  if (
    fields.escalated === true &&
    !isSupplied(fields.discrepancy_detail) &&
    !isSupplied(fields.medication_event_ref)
  ) {
    pushViolation(
      out,
      pack,
      'escalated_requires_detail_or_event_ref',
      'discrepancy_detail',
      'escalated=true',
    );
  }
}

/**
 * Personal care: an observed change in condition is the deterioration signal.
 *
 * The pack declares `condition_change_observed` precisely so a decline is
 * visible, but nothing made the worker do anything with it. Recorded and then
 * dropped is worse than not recorded — it reads as "someone looked and it was
 * fine" in every downstream review.
 *
 * The transfer rule is manual handling: at MODERATE or FULL assistance the
 * client is being physically moved, and how is the whole safety question. The
 * field permits "not applicable" for the levels where nobody is lifted; using
 * that at FULL assistance is the bypass worth refusing.
 */
function checkPersonalCareAdl(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  notApplicableFields: ReadonlySet<string>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'PERSONAL_CARE_ADL') return;

  if (fields.condition_change_observed === true) {
    // Any one of these closes the loop: an incident, an escalation, or a
    // written account of what changed. None of them present means nothing
    // happened next.
    const followedUp =
      isSupplied(fields.adverse_event_incident_ref) ||
      fields.escalated === true ||
      isSupplied(fields.material_variance_note);
    if (!followedUp) {
      pushViolation(
        out,
        pack,
        'condition_change_requires_follow_up',
        'material_variance_note',
        'condition_change_observed=true',
      );
    }
  }

  const PHYSICAL_TRANSFER_LEVELS: ReadonlySet<string> = new Set(['MODERATE', 'FULL']);
  if (
    typeof fields.assistance_level === 'string' &&
    PHYSICAL_TRANSFER_LEVELS.has(fields.assistance_level) &&
    (notApplicableFields.has('transfer_method_used') || !isSupplied(fields.transfer_method_used))
  ) {
    pushViolation(
      out,
      pack,
      'physical_assistance_requires_transfer_method',
      'transfer_method_used',
      `assistance_level=${fields.assistance_level}`,
    );
  }

  // A refusal or partial reason contradicts a clean completion. The two fields
  // disagreeing means one of them is wrong, and finance reads the outcome.
  if (isSupplied(fields.refusal_or_partial_reason) && fields.delivery_outcome === 'DELIVERED') {
    pushViolation(
      out,
      pack,
      'refusal_reason_contradicts_delivered_outcome',
      'delivery_outcome',
      'refusal_or_partial_reason supplied',
    );
  }
}

/**
 * Transport legs that never left or never arrived need an incident or no-show
 * note; a silent false is indistinguishable from a completed journey.
 */
function checkTransportDirect(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'TRANSPORT_DIRECT') return;

  if (fields.destination_reached === false && !isSupplied(fields.no_show_or_incident)) {
    pushViolation(
      out,
      pack,
      'destination_reached_false_requires_no_show_or_incident',
      'no_show_or_incident',
      'destination_reached=false',
    );
  }

  if (fields.pickup_confirmed === false && !isSupplied(fields.no_show_or_incident)) {
    pushViolation(
      out,
      pack,
      'pickup_confirmed_false_requires_no_show_or_incident',
      'no_show_or_incident',
      'pickup_confirmed=false',
    );
  }
}

/**
 * Meal delivery outcomes of no-contact or not-delivered must record what the
 * worker then did. Otherwise the failure has no operational trail.
 */
function checkMealDelivery(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'MEAL_DELIVERY') return;

  const detail = fields.delivery_outcome_detail;
  if (detail === 'NO_CONTACT' && !isSupplied(fields.no_contact_action_taken)) {
    pushViolation(
      out,
      pack,
      'delivery_outcome_detail_no_contact_requires_action',
      'no_contact_action_taken',
      'delivery_outcome_detail=NO_CONTACT',
    );
  }

  if (detail === 'NOT_DELIVERED' && !isSupplied(fields.no_contact_action_taken)) {
    pushViolation(
      out,
      pack,
      'delivery_outcome_detail_not_delivered_requires_action',
      'no_contact_action_taken',
      'delivery_outcome_detail=NOT_DELIVERED',
    );
  }
}

/**
 * Meals: the diet-control and intake rules that current fields can carry.
 *
 * S3.2 was scoped as "meals plus a swallow/texture/allergy overlay". The
 * overlay is NOT built here, and the reason is structural rather than an
 * omission. A texture or allergy control exists because a specific client is
 * at risk, and the rule worth enforcing is "if a control was required then it
 * was met". Nothing in either schema says a control is REQUIRED:
 * `texture_modification_applied` records what was done, never what was needed,
 * and MEAL_DELIVERY carries no diet, texture or allergy field at all.
 *
 * Writing "texture was correct" without a declared "texture was required"
 * signal would be an invariant that cannot fire on the case it exists for,
 * while reading in review as though the risk were covered. The gap is recorded
 * in the ledger: it needs a plan-derived requirement flag and a clinical ruling
 * on what counts as required, neither of which is an invariant change.
 */
function checkMeals(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  notApplicableFields: ReadonlySet<string>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack === 'MEAL_PREPARATION') {
    if (fields.diet_controls_followed === false && !isSupplied(fields.substitution_or_escalation)) {
      pushViolation(
        out,
        pack,
        'diet_controls_not_followed_requires_substitution_or_escalation',
        'substitution_or_escalation',
        'diet_controls_followed=false',
      );
    }

    // NONE is a real observation — the client ate nothing — and is deliberately
    // distinct from NOT_OBSERVED. It can mean refusal, illness, or that the
    // meal was unsafe to swallow, and all three need an account of what the
    // worker did. Requiring a written response is coherence; requiring a
    // clinical escalation would be a clinical ruling and is not made here.
    if (
      fields.client_intake_observed === 'NONE' &&
      !isSupplied(fields.substitution_or_escalation)
    ) {
      pushViolation(
        out,
        pack,
        'nil_intake_requires_substitution_or_escalation',
        'substitution_or_escalation',
        'client_intake_observed=NONE',
      );
    }

    // Applying a texture modification IS following a diet control. Recording
    // one while declaring controls unfollowed or not applicable is two fields
    // contradicting each other, and the not-applicable route is the blanket
    // bypass the design forbids — it is how a client with a real swallow
    // requirement ends up with no control recorded at all.
    if (
      isSupplied(fields.texture_modification_applied) &&
      (fields.diet_controls_followed === false || notApplicableFields.has('diet_controls_followed'))
    ) {
      pushViolation(
        out,
        pack,
        'texture_modification_contradicts_diet_controls',
        'diet_controls_followed',
        'texture_modification_applied supplied',
      );
    }
  }

  if (pack === 'MEAL_DELIVERY') {
    // `meals_delivered` is the billable quantity for this pack and had none of
    // the money coherence the item and expense packs got. A bundle could say
    // DELIVERED at the top, NOT_DELIVERED in the detail, and still claim meals.
    const meals = asFiniteNumber(fields.meals_delivered);
    const failedDetail =
      fields.delivery_outcome_detail === 'NOT_DELIVERED' ||
      fields.delivery_outcome_detail === 'NO_CONTACT';
    if (meals !== null && meals > 0 && failedDetail) {
      pushViolation(
        out,
        pack,
        'meals_claimed_on_failed_delivery_detail',
        'meals_delivered',
        `delivery_outcome_detail=${String(fields.delivery_outcome_detail)} with meals_delivered>0`,
      );
    }
    // Independent of the quantity. A delivery that did not happen is not a
    // clean delivery whether or not any meals were claimed, and nesting this
    // under the quantity check meant zero meals silently passed.
    if (failedDetail && fields.delivery_outcome === 'DELIVERED') {
      pushViolation(
        out,
        pack,
        'failed_delivery_detail_contradicts_delivered_outcome',
        'delivery_outcome',
        `delivery_outcome_detail=${String(fields.delivery_outcome_detail)}`,
      );
    }
    if (
      meals !== null &&
      meals > 0 &&
      typeof fields.delivery_outcome === 'string' &&
      ADVERSE_DELIVERY_OUTCOME_SET.has(fields.delivery_outcome)
    ) {
      pushViolation(
        out,
        pack,
        'quantity_contradicts_adverse_outcome',
        'delivery_outcome',
        `meals_delivered>0 with delivery_outcome=${fields.delivery_outcome}`,
      );
    }

    // A failed hot-hold or cold-chain check still reads as a clean delivery to
    // anything that looks only at the outcome enum.
    if (fields.temperature_check_passed === false && !isSupplied(fields.material_variance_note)) {
      pushViolation(
        out,
        pack,
        'temperature_check_failed_requires_material_variance_note',
        'material_variance_note',
        'temperature_check_passed=false',
      );
    }
  }
}

/**
 * Respite that ends without a returning carer or without an incoming handover
 * must leave outstanding actions — otherwise the gap is unowned.
 */
function checkRespite(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'RESPITE') return;

  if (fields.carer_returned === false && !isSupplied(fields.outstanding_actions)) {
    pushViolation(
      out,
      pack,
      'carer_returned_false_requires_outstanding_actions',
      'outstanding_actions',
      'carer_returned=false',
    );
  }

  if (fields.incoming_handover_received === false && !isSupplied(fields.outstanding_actions)) {
    pushViolation(
      out,
      pack,
      'incoming_handover_false_requires_outstanding_actions',
      'outstanding_actions',
      'incoming_handover_received=false',
    );
  }
}

/**
 * The swallow/texture/allergy overlay is the "control is required" signal.
 *
 * S3.2 recorded the overlay half of the meals work as unbuildable because
 * nothing in the evidence said a control was REQUIRED — only what was applied.
 * That was true of the pack fields and wrong about the system: overlays are
 * exactly that signal, and `SWALLOW_TEXTURE_ALLERGY` is a declared one that
 * must be switched on by an active record rather than inferred from a label.
 *
 * With the overlay active the rule is the one that was wanted all along: a
 * client known to be at risk of choking or aspiration cannot have a meal
 * recorded with diet controls unfollowed, declared not applicable, or a texture
 * left blank. The not-applicable route matters most — it is how a real swallow
 * requirement ends up with no control recorded anywhere.
 */
function checkSwallowTextureAllergyOverlay(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  notApplicableFields: ReadonlySet<string>,
  activeOverlays: ReadonlySet<string>,
  out: EvidenceInvariantViolation[],
): void {
  if (!activeOverlays.has('SWALLOW_TEXTURE_ALLERGY')) return;

  // MEAL_DELIVERY declares no diet, texture or allergy field, so there is
  // nothing here to check that the meal was safe for this client.
  //
  // Both review engines reached the same answer independently: do NOT add a
  // texture attestation to the delivery pack. A driver who did not cook the
  // meal cannot truthfully verify its texture, and a field they cannot answer
  // produces a guess or a habitual yes — a fake safeguard, which is worse than
  // an absent one. The right design is an upstream verification record from the
  // preparer plus an identity match at the door, and that needs schema this
  // pack does not have.
  //
  // Until it does, a delivered meal for a client at risk of choking is refused
  // rather than silently accepted. The refusal names the missing capability
  // instead of blaming the worker.
  if (pack === 'MEAL_DELIVERY') {
    if (
      fields.delivery_outcome_detail === 'HANDED_TO_CLIENT' ||
      fields.delivery_outcome_detail === 'LEFT_AS_AGREED'
    ) {
      pushViolation(
        out,
        pack,
        'swallow_overlay_cannot_be_evidenced_on_delivery',
        'delivery_outcome_detail',
        'SWALLOW_TEXTURE_ALLERGY overlay active on a delivery pack that records no diet, texture or allergy control',
      );
    }
    return;
  }

  if (pack !== 'MEAL_PREPARATION') return;

  if (fields.diet_controls_followed !== true) {
    pushViolation(
      out,
      pack,
      'swallow_overlay_requires_diet_controls_followed',
      'diet_controls_followed',
      'SWALLOW_TEXTURE_ALLERGY overlay active',
    );
  }

  if (notApplicableFields.has('diet_controls_followed')) {
    pushViolation(
      out,
      pack,
      'swallow_overlay_forbids_diet_controls_not_applicable',
      'diet_controls_followed',
      'SWALLOW_TEXTURE_ALLERGY overlay active',
    );
  }

  if (
    !isSupplied(fields.texture_modification_applied) ||
    notApplicableFields.has('texture_modification_applied')
  ) {
    pushViolation(
      out,
      pack,
      'swallow_overlay_requires_texture_modification_recorded',
      'texture_modification_applied',
      'SWALLOW_TEXTURE_ALLERGY overlay active',
    );
  }
}

/**
 * Respite is a custody chain. The client is someone else's responsibility
 * before it starts and someone else's again when it ends, and the two ends are
 * where a client is left unattended.
 *
 * S2.1 already required outstanding actions when the carer did not return or
 * no incoming handover arrived. What was missing is the closing end: respite
 * that finishes with no outgoing handover AND no returning carer means nobody
 * took the client back. Either the responsibility window was never confirmed
 * or it was left open, and both are the same operational hole.
 */
function checkRespiteCustody(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'RESPITE') return;

  // Nobody received the client back. Handing over to a returning carer and
  // handing over to an incoming worker are the only two exits.
  if (fields.outgoing_handover_given === false && fields.carer_returned === false) {
    pushViolation(
      out,
      pack,
      'no_outgoing_handover_and_no_carer_return_leaves_client_unattended',
      'outgoing_handover_given',
      'outgoing_handover_given=false and carer_returned=false',
    );
  }

  // An unconfirmed responsibility window means the worker never established
  // when their duty of care began or ended. Recording a completed respite on
  // top of that is claiming a period nobody owned.
  if (fields.responsibility_window_confirmed === false && !isSupplied(fields.outstanding_actions)) {
    pushViolation(
      out,
      pack,
      'unconfirmed_responsibility_window_requires_outstanding_actions',
      'outstanding_actions',
      'responsibility_window_confirmed=false',
    );
  }
}

/**
 * Transport: the vulnerable moment is arrival, not the journey.
 *
 * S2.1 covered the failure cases — no pickup, no destination. This covers the
 * one that looks like success: the client was delivered somewhere and simply
 * left. `handover_at_destination` permits "not applicable" because some trips
 * end with an independent client at their own front door, but using that route
 * has to be a declared choice rather than a blank field.
 */
function checkTransportArrival(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  notApplicableFields: ReadonlySet<string>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'TRANSPORT_DIRECT') return;

  if (
    fields.destination_reached === true &&
    !isSupplied(fields.handover_at_destination) &&
    !notApplicableFields.has('handover_at_destination')
  ) {
    pushViolation(
      out,
      pack,
      'destination_reached_requires_handover_or_explicit_not_applicable',
      'handover_at_destination',
      'destination_reached=true',
    );
  }

  // Legs are the billable quantity for this pack. Claiming completed legs on a
  // journey that never reached its destination, with nothing recorded about
  // why, is a quantity nobody can substantiate.
  const legs = asFiniteNumber(fields.legs_completed);
  if (fields.destination_reached === false && legs !== null && legs > 0) {
    if (!isSupplied(fields.no_show_or_incident)) {
      pushViolation(
        out,
        pack,
        'legs_claimed_without_destination_requires_no_show_or_incident',
        'no_show_or_incident',
        'destination_reached=false with legs_completed>0',
      );
    }
    // A note explains the failure; it does not make the journey a delivery.
    // Without this, free text cleared the gate while the billable quantity and
    // a DELIVERED outcome both survived a journey that never arrived.
    if (fields.delivery_outcome === 'DELIVERED') {
      pushViolation(
        out,
        pack,
        'legs_claimed_without_destination_contradicts_delivered_outcome',
        'delivery_outcome',
        'destination_reached=false with legs_completed>0',
      );
    }
  }
}

/**
 * Domestic and maintenance work that leave a site unsafe (or find a hazard)
 * must explain the variance. A silent false/true is an unaccountable risk.
 */
function checkSiteSafety(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (!SITE_SAFETY_PACKS.has(pack)) return;

  if (fields.site_left_safe === false && !isSupplied(fields.material_variance_note)) {
    pushViolation(
      out,
      pack,
      'site_left_safe_false_requires_material_variance_note',
      'material_variance_note',
      'site_left_safe=false',
    );
  }

  if (
    pack === 'MAINTENANCE_WORK' &&
    fields.hazard_or_defect_found === true &&
    !isSupplied(fields.material_variance_note)
  ) {
    pushViolation(
      out,
      pack,
      'hazard_or_defect_requires_material_variance_note',
      'material_variance_note',
      'hazard_or_defect_found=true',
    );
  }
}

/**
 * Clinical work that did not happen as ordered.
 *
 * Every one of these packs records an outcome enum saying the ordered or
 * delegated work was varied or not done, and every one of them previously let
 * that sit alone. The enum is the only trace, and nothing downstream reads it —
 * reports key off the completed visit and the linked order, both of which are
 * still present. A varied order and a clean one look identical.
 *
 * The delivery-outcome rules are the sharper half: recording NOT_PERFORMED,
 * NOT_COMPLETED, PARTIAL or NOT_DONE against a DELIVERED outcome is two fields
 * contradicting each other, and finance reads the outcome.
 *
 * MODIFIED is deliberately excluded from that set, for nursing and for the
 * allied assistant. Both audit engines argued it belonged. It does not: a
 * modified intervention is one the clinician adapted and still delivered, and
 * adaptation is normal clinical practice rather than a shortfall. Forcing it to
 * a non-DELIVERED outcome would under-record real work and train people to
 * avoid the honest enum. It still requires a variance note, which is where the
 * adaptation gets explained.
 */
function checkClinicalExecution(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack === 'NURSING_CLINICAL') {
    const performed = fields.intervention_performed;
    if (
      (performed === 'MODIFIED' || performed === 'NOT_PERFORMED') &&
      !isSupplied(fields.material_variance_note)
    ) {
      pushViolation(
        out,
        pack,
        'varied_intervention_requires_material_variance_note',
        'material_variance_note',
        `intervention_performed=${String(performed)}`,
      );
    }
    if (performed === 'NOT_PERFORMED' && fields.delivery_outcome === 'DELIVERED') {
      pushViolation(
        out,
        pack,
        'not_performed_contradicts_delivered_outcome',
        'delivery_outcome',
        'intervention_performed=NOT_PERFORMED',
      );
    }
  }

  if (pack === 'DELEGATED_CLINICAL_SUPPORT') {
    const outcome = fields.delegated_task_outcome;
    if (
      (outcome === 'PARTIAL' || outcome === 'NOT_COMPLETED') &&
      !isSupplied(fields.variance_from_delegation)
    ) {
      // Distinct from checkClinicalFollowUp, which keys on follow_up_required.
      // Work can fall short of the delegation without anyone judging that
      // follow-up is needed, and that is exactly the case worth catching.
      pushViolation(
        out,
        pack,
        'incomplete_delegation_requires_variance_from_delegation',
        'variance_from_delegation',
        `delegated_task_outcome=${String(outcome)}`,
      );
    }
    // PARTIAL is included: work that literally did not finish cannot read as a
    // full delivery to anything that keys finance off the outcome. MODIFIED on
    // the other packs is deliberately NOT treated this way — see below.
    if (
      (outcome === 'NOT_COMPLETED' || outcome === 'PARTIAL') &&
      fields.delivery_outcome === 'DELIVERED'
    ) {
      pushViolation(
        out,
        pack,
        'incomplete_delegation_contradicts_delivered_outcome',
        'delivery_outcome',
        `delegated_task_outcome=${String(outcome)}`,
      );
    }
    // RULING (Paul, 2026-07-28): every shortfall against a delegation must
    // reach the delegating clinician, not only a total failure. A partial is
    // still work that did not happen as delegated, and the person who owns the
    // delegation is the only one who can judge whether the remainder matters.
    if (
      (outcome === 'NOT_COMPLETED' || outcome === 'PARTIAL') &&
      fields.supervisor_notified !== true
    ) {
      pushViolation(
        out,
        pack,
        'incomplete_delegation_requires_supervisor_notified',
        'supervisor_notified',
        `delegated_task_outcome=${String(outcome)}`,
      );
    }
  }

  if (pack === 'ALLIED_ASSISTANT') {
    const activity = fields.activity_completed;
    if (
      (activity === 'MODIFIED' || activity === 'NOT_DONE') &&
      !isSupplied(fields.material_variance_note)
    ) {
      pushViolation(
        out,
        pack,
        'varied_activity_requires_material_variance_note',
        'material_variance_note',
        `activity_completed=${String(activity)}`,
      );
    }
    if (activity === 'NOT_DONE' && fields.delivery_outcome === 'DELIVERED') {
      pushViolation(
        out,
        pack,
        'activity_not_done_contradicts_delivered_outcome',
        'delivery_outcome',
        'activity_completed=NOT_DONE',
      );
    }
    // An assistant works to a therapist's plan under delegation. Asking for
    // supervisor feedback and then leaving no account of what happened gives
    // the supervisor nothing to respond to.
    if (fields.supervisor_feedback_required === true && !isSupplied(fields.client_tolerance)) {
      pushViolation(
        out,
        pack,
        'supervisor_feedback_requires_client_tolerance',
        'client_tolerance',
        'supervisor_feedback_required=true',
      );
    }
  }

  if (pack === 'ALLIED_PROFESSIONAL' && fields.follow_up_required === true) {
    // The allied pack has no adverse-event route and no variance field of its
    // own; advice_given is the one place a next action can live.
    if (!isSupplied(fields.advice_given) && !isSupplied(fields.material_variance_note)) {
      pushViolation(
        out,
        pack,
        'follow_up_requires_advice_or_variance_note',
        'advice_given',
        'follow_up_required=true',
      );
    }
  }
}

/**
 * Clinical follow-up requires a narrative of response (nursing) or of
 * variance from the delegated task (delegated support). A bare true with no
 * explanation cannot drive the next clinical action.
 */
function checkClinicalFollowUp(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (fields.follow_up_required !== true) return;

  if (pack === 'NURSING_CLINICAL' && !isSupplied(fields.client_response)) {
    pushViolation(
      out,
      pack,
      'follow_up_requires_client_response',
      'client_response',
      'follow_up_required=true',
    );
  }

  if (pack === 'DELEGATED_CLINICAL_SUPPORT' && !isSupplied(fields.variance_from_delegation)) {
    pushViolation(
      out,
      pack,
      'follow_up_requires_variance_from_delegation',
      'variance_from_delegation',
      'follow_up_required=true',
    );
  }
}

/**
 * Phase 4 — the remaining packs.
 *
 * The two defect classes this covers are money and safeguarding.
 *
 * Money: several of these packs carry the billable quantity directly —
 * `participant_count_present`, `quantity_supplied`, `receipt_amount_observed`.
 * Where a pack records both a quantity and a signal that the thing was not
 * received, both surviving alongside a DELIVERED outcome is a claim nobody can
 * substantiate. That is the same shape as the transport legs rule.
 *
 * Safeguarding: `PERSONAL_AFFAIRS` is the pack a worker uses while handling
 * somebody else's money and paperwork, and `coercion_or_pressure_concern` is
 * the field where elder financial abuse first shows up. Recorded and dropped is
 * the failure mode, so it must reach somebody.
 */
function checkPhase4Packs(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  const delivered = fields.delivery_outcome === 'DELIVERED';

  if (pack === 'SOCIAL_GROUP') {
    const present = asFiniteNumber(fields.participant_count_present);
    if (present !== null && present <= 0) {
      if (!isSupplied(fields.session_exception)) {
        pushViolation(
          out,
          pack,
          'empty_session_requires_session_exception',
          'session_exception',
          'participant_count_present=0',
        );
      }
      // Attendance is what a group session is. Nobody present is not a
      // delivered session, whatever else was set up.
      if (delivered) {
        pushViolation(
          out,
          pack,
          'empty_session_contradicts_delivered_outcome',
          'delivery_outcome',
          'participant_count_present=0',
        );
      }
    }
  }

  if (pack === 'SOCIAL_INDIVIDUAL' && fields.client_engagement === 'PARTIALLY_ENGAGED') {
    if (!isSupplied(fields.variance_reason)) {
      pushViolation(
        out,
        pack,
        'partial_engagement_requires_variance_reason',
        'variance_reason',
        'client_engagement=PARTIALLY_ENGAGED',
      );
    }
  }

  if (pack === 'SOCIAL_INDIVIDUAL' && fields.client_engagement === 'DECLINED') {
    if (!isSupplied(fields.variance_reason)) {
      pushViolation(
        out,
        pack,
        'declined_engagement_requires_variance_reason',
        'variance_reason',
        'client_engagement=DECLINED',
      );
    }
    if (delivered) {
      pushViolation(
        out,
        pack,
        'declined_engagement_contradicts_delivered_outcome',
        'delivery_outcome',
        'client_engagement=DECLINED',
      );
    }
  }

  if (pack === 'PERSONAL_AFFAIRS' && fields.coercion_or_pressure_concern === true) {
    // This is the field where elder financial abuse first appears. A concern
    // that reaches nobody is the whole failure mode, so it needs both a
    // written account and an owner for what happens next.
    if (!isSupplied(fields.material_variance_note)) {
      pushViolation(
        out,
        pack,
        'coercion_concern_requires_material_variance_note',
        'material_variance_note',
        'coercion_or_pressure_concern=true',
      );
    }
    if (fields.follow_up_required !== true) {
      pushViolation(
        out,
        pack,
        'coercion_concern_requires_follow_up',
        'follow_up_required',
        'coercion_or_pressure_concern=true',
      );
    }
  }

  if (pack === 'TRANSPORT_INDIRECT') {
    const outcome = fields.arrangement_outcome;
    // RULING (Paul, 2026-07-28): billing follows redemption, not arrangement.
    // An issued voucher nobody used is not a delivered service, so
    // ISSUED_UNUSED joins the non-delivery set. Only REDEEMED can be DELIVERED.
    if (outcome === 'CANCELLED' || outcome === 'FAILED' || outcome === 'ISSUED_UNUSED') {
      if (!isSupplied(fields.exception_note)) {
        pushViolation(
          out,
          pack,
          'unredeemed_arrangement_requires_exception_note',
          'exception_note',
          `arrangement_outcome=${String(outcome)}`,
        );
      }
      if (delivered) {
        pushViolation(
          out,
          pack,
          'unredeemed_arrangement_contradicts_delivered_outcome',
          'delivery_outcome',
          `arrangement_outcome=${String(outcome)}`,
        );
      }
    }
  }

  if (pack === 'DOMESTIC') {
    const status = fields.task_status;
    if ((status === 'PARTIAL' || status === 'REFUSED') && !isSupplied(fields.incomplete_reason)) {
      pushViolation(
        out,
        pack,
        'incomplete_task_status_requires_incomplete_reason',
        'incomplete_reason',
        `task_status=${String(status)}`,
      );
    }
    // PARTIAL is included for the same reason it is on delegated clinical
    // work: task_status is the honest enum and delivery_outcome is what
    // finance reads. Letting them disagree makes the honest answer free.
    if ((status === 'REFUSED' || status === 'PARTIAL') && delivered) {
      pushViolation(
        out,
        pack,
        'incomplete_task_contradicts_delivered_outcome',
        'delivery_outcome',
        `task_status=${String(status)}`,
      );
    }
    // A hazard seen in someone's home is only useful if it leaves the visit.
    if (fields.household_hazard_observed === true && !isSupplied(fields.material_variance_note)) {
      pushViolation(
        out,
        pack,
        'household_hazard_requires_material_variance_note',
        'material_variance_note',
        'household_hazard_observed=true',
      );
    }
  }

  if (pack === 'MAINTENANCE_WORK') {
    if (fields.site_left_safe === false && !isSupplied(fields.work_incomplete_reason)) {
      // Already covered by checkSiteSafety for the variance note; this is the
      // separate question of why the work stopped in that state.
      pushViolation(
        out,
        pack,
        'unsafe_site_requires_work_incomplete_reason',
        'work_incomplete_reason',
        'site_left_safe=false',
      );
    }
    // Writing down why the work did not finish, then claiming a full delivery,
    // is the same contradiction as a refusal reason on a delivered visit.
    if (isSupplied(fields.work_incomplete_reason) && delivered) {
      pushViolation(
        out,
        pack,
        'work_incomplete_reason_contradicts_delivered_outcome',
        'delivery_outcome',
        'work_incomplete_reason supplied',
      );
    }
  }

  // The two expense packs are pass-through money: the provider paid a supplier
  // and claims it back. `receipt_amount_observed` is the amount, and it is the
  // one field here that becomes a real payment. An observed amount surviving a
  // visit that did not deliver is a reimbursement for something that did not
  // happen, so the outcome and the money have to agree.
  if (capturesReceiptEvidence(pack)) {
    const amount = asFiniteNumber(fields.receipt_amount_observed);
    if (amount !== null && amount > 0) {
      const outcome = fields.delivery_outcome;
      if (typeof outcome === 'string' && ADVERSE_DELIVERY_OUTCOME_SET.has(outcome)) {
        // A hard contradiction, not a note-gated one. Requiring an exception
        // note let free text override an authority outcome: a NO_CONTACT visit
        // became a valid reimbursement the moment somebody typed anything.
        // That is the local-override failure this whole path exists to stop,
        // and it was reachable with real money on the other side.
        pushViolation(
          out,
          pack,
          'expense_amount_contradicts_adverse_outcome',
          'delivery_outcome',
          `receipt_amount_observed>0 with delivery_outcome=${outcome}`,
        );
      }
    }
    // A zero or negative observed amount is not an expense. Left alone it is a
    // receipt reference attached to nothing, which reads as substantiated.
    if (amount !== null && amount <= 0 && !isSupplied(fields.exception_note)) {
      pushViolation(
        out,
        pack,
        'non_positive_expense_amount_requires_exception_note',
        'exception_note',
        'receipt_amount_observed<=0',
      );
    }
  }

  if (pack === 'CLINICAL_ITEM') {
    const quantity = asFiniteNumber(fields.quantity_supplied);
    if (fields.recipient_confirmed === false) {
      if (!isSupplied(fields.shortfall_reason)) {
        pushViolation(
          out,
          pack,
          'unconfirmed_recipient_requires_shortfall_reason',
          'shortfall_reason',
          'recipient_confirmed=false',
        );
      }
      // Items nobody confirmed receiving, billed as delivered.
      if (delivered && quantity !== null && quantity > 0) {
        pushViolation(
          out,
          pack,
          'unconfirmed_recipient_contradicts_delivered_quantity',
          'delivery_outcome',
          'recipient_confirmed=false with quantity_supplied>0',
        );
      }
    }
    if (quantity !== null && quantity <= 0 && !isSupplied(fields.shortfall_reason)) {
      pushViolation(
        out,
        pack,
        'zero_quantity_requires_shortfall_reason',
        'shortfall_reason',
        'quantity_supplied=0',
      );
    }
    if (
      quantity !== null &&
      quantity > 0 &&
      typeof fields.delivery_outcome === 'string' &&
      ADVERSE_DELIVERY_OUTCOME_SET.has(fields.delivery_outcome)
    ) {
      // Same correction as the expense packs: a reason string explains why the
      // supply failed, it does not make the quantity claimable.
      pushViolation(
        out,
        pack,
        'quantity_contradicts_adverse_outcome',
        'delivery_outcome',
        `quantity_supplied>0 with delivery_outcome=${fields.delivery_outcome}`,
      );
    }
  }

  if (pack === 'NUTRITION_ITEM') {
    const quantity = asFiniteNumber(fields.quantity_supplied);
    if (fields.receipt_confirmed === false && !isSupplied(fields.substitution_reason)) {
      pushViolation(
        out,
        pack,
        'unconfirmed_receipt_requires_substitution_reason',
        'substitution_reason',
        'receipt_confirmed=false',
      );
    }
    if (fields.receipt_confirmed === false && delivered && quantity !== null && quantity > 0) {
      pushViolation(
        out,
        pack,
        'unconfirmed_receipt_contradicts_delivered_quantity',
        'delivery_outcome',
        'receipt_confirmed=false with quantity_supplied>0',
      );
    }
    if (
      quantity !== null &&
      quantity > 0 &&
      typeof fields.delivery_outcome === 'string' &&
      ADVERSE_DELIVERY_OUTCOME_SET.has(fields.delivery_outcome)
    ) {
      pushViolation(
        out,
        pack,
        'quantity_contradicts_adverse_outcome',
        'delivery_outcome',
        `quantity_supplied>0 with delivery_outcome=${fields.delivery_outcome}`,
      );
    }
  }

  if (pack === 'CONTINENCE_NONCLINICAL' && fields.skin_or_condition_concern === true) {
    // Skin integrity is the precursor to a pressure injury. A concern seen by
    // a non-clinical worker has to reach someone who can act on it.
    if (fields.escalated !== true) {
      pushViolation(
        out,
        pack,
        'skin_concern_requires_escalation',
        'escalated',
        'skin_or_condition_concern=true',
      );
    }
  }

  if (pack === 'AT_HM_LIFECYCLE') {
    const outcome = fields.stage_outcome;
    if ((outcome === 'BLOCKED' || outcome === 'DEFERRED') && !isSupplied(fields.defect_or_issue)) {
      pushViolation(
        out,
        pack,
        'blocked_or_deferred_stage_requires_defect_or_issue',
        'defect_or_issue',
        `stage_outcome=${String(outcome)}`,
      );
    }
    // Procurement and installation are where money and equipment change hands.
    // A completed one with nothing attached is unsubstantiated.
    const DOCUMENTED_STAGES: ReadonlySet<string> = new Set(['PROCUREMENT', 'INSTALLATION']);
    if (
      typeof fields.milestone_stage === 'string' &&
      DOCUMENTED_STAGES.has(fields.milestone_stage) &&
      outcome === 'COMPLETED' &&
      !isSupplied(fields.supporting_document_ref)
    ) {
      pushViolation(
        out,
        pack,
        'completed_procurement_requires_supporting_document',
        'supporting_document_ref',
        `milestone_stage=${fields.milestone_stage} with stage_outcome=COMPLETED`,
      );
    }
  }
}

/**
 * Care management (S5.1) — the non-visit pack, where time is the claim.
 *
 * Every other pack bills against something that physically happened at an
 * address. This one bills against `activity_duration`, a number a coordinator
 * types about their own work, with no client present to corroborate it. That
 * makes duration the single most claimable field in the whole contract, and a
 * non-positive or absent one recorded as a delivered activity is time billed
 * that nobody spent.
 *
 * The follow-up rule is the other half. Care management exists to make sure
 * something happens next; `follow_up_required` with no `action_owner` is a
 * commitment recorded against nobody, which is how a coordination task dies
 * quietly between two people who each thought the other had it.
 */
function checkCareManagement(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  out: EvidenceInvariantViolation[],
): void {
  if (pack !== 'CARE_MANAGEMENT') return;

  if (fields.follow_up_required === true && !isSupplied(fields.action_owner)) {
    pushViolation(
      out,
      pack,
      'follow_up_requires_action_owner',
      'action_owner',
      'follow_up_required=true',
    );
  }

  const duration = asFiniteNumber(fields.activity_duration);
  if (duration !== null && duration <= 0) {
    if (fields.delivery_outcome === 'DELIVERED') {
      pushViolation(
        out,
        pack,
        'zero_duration_contradicts_delivered_outcome',
        'delivery_outcome',
        'activity_duration<=0',
      );
    }
    if (!isSupplied(fields.material_variance_note)) {
      pushViolation(
        out,
        pack,
        'zero_duration_requires_material_variance_note',
        'material_variance_note',
        'activity_duration<=0',
      );
    }
  }

  // A referral that records no destination is not a referral. `outcome` is
  // required by the schema, so this asks the narrower question: where did it
  // go, and can anyone follow it.
  if (
    fields.activity_type === 'REFERRAL' &&
    !isSupplied(fields.source_evidence_ref) &&
    !isSupplied(fields.action_owner)
  ) {
    pushViolation(
      out,
      pack,
      'referral_requires_owner_or_source_evidence',
      'action_owner',
      'activity_type=REFERRAL',
    );
  }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Returns every cross-field invariant violation for the pack and field bundle.
 * Rules are additive: several may fire at once. An empty array means the
 * combination is coherent (not that per-field requirements are satisfied).
 */
export function findEvidenceInvariantViolations(
  pack: ServicePackCode,
  fields: Readonly<Record<string, unknown>>,
  /**
   * Keys the bundle explicitly claimed as not applicable.
   *
   * Some rules must distinguish "not supplied" from "declared not applicable":
   * marking a manual-handling transfer method N/A while recording full physical
   * assistance is a deliberate bypass, not an omission, and reads differently.
   */
  context: EvidenceInvariantContext,
): EvidenceInvariantViolation[] {
  const out: EvidenceInvariantViolation[] = [];
  const notApplicable = new Set(context.notApplicableFields ?? []);
  const overlays: ReadonlySet<string> = new Set(context.activeOverlays);

  checkAdverseDeliveryOutcome(pack, fields, out);
  checkEscalationDisposition(pack, fields, out);
  checkAdverseEvent(pack, fields, out);
  checkMedicationSelfAdmin(pack, fields, out);
  checkTransportDirect(pack, fields, out);
  checkMealDelivery(pack, fields, out);
  checkRespite(pack, fields, out);
  checkSiteSafety(pack, fields, out);
  checkClinicalFollowUp(pack, fields, out);
  checkClinicalExecution(pack, fields, out);
  checkPersonalCareAdl(pack, fields, notApplicable, out);
  checkMeals(pack, fields, notApplicable, out);
  checkRespiteCustody(pack, fields, out);
  checkTransportArrival(pack, fields, notApplicable, out);
  checkSwallowTextureAllergyOverlay(pack, fields, notApplicable, overlays, out);
  checkPhase4Packs(pack, fields, out);
  checkCareManagement(pack, fields, out);

  return out;
}
